import { useCallback, useEffect, useState, type FormEvent } from 'react';
import {
  Bouton, Carte, CaseACocher, Champ, EnTetePage, Etiquette, IconeCheck,
  IconeDupliquer, IconeSecurite, Message, Squelette, useRetours,
} from '@journal/ui';
import { QrCode } from '../components/QrCode.tsx';
import { messageErreur, useSession } from '../lib/session.tsx';
import type { EtatDoubleAuthentification, UtilisateurCourant } from '../lib/api.ts';

/**
 * Sécurité du compte.
 *
 * ── Les codes de récupération ne s'affichent qu'une fois ────────────────────
 * C'est le point où l'on perd des utilisateurs : ils activent la double
 * authentification, ferment la page, changent de téléphone, et n'ont plus aucun
 * moyen d'entrer. L'écran le dit avant l'activation, pas après, et refuse de se
 * refermer tant que la copie n'est pas confirmée.
 *
 * ── Un QR code à scanner, la clé en secours ─────────────────────────────────
 * Les applications d'authentification attendent un QR code : c'est ce qu'on
 * montre d'abord. La clé reste affichée dessous, repliée : sur un poste sans
 * téléphone à portée, ou dans un gestionnaire de mots de passe, c'est le seul
 * moyen d'ajouter le compte.
 */

/**
 * Copie dans le presse-papiers.
 *
 * `navigator.clipboard` n'existe pas hors contexte sécurisé — un accès en
 * `http://` sur le réseau de l'école, cas tout à fait ordinaire. On le dit
 * plutôt que de laisser un bouton sans effet : l'utilisateur croirait avoir
 * copié sa clé, et collerait autre chose.
 */
async function copier(texte: string): Promise<boolean> {
  try {
    if (!navigator.clipboard) return false;
    await navigator.clipboard.writeText(texte);
    return true;
  } catch {
    return false;
  }
}

export function Securite() {
  const { api } = useSession();
  const { notifier } = useRetours();

  async function copierEtSignaler(texte: string, quoi: string) {
    if (await copier(texte)) notifier(`${quoi} copié.`);
    else {
      notifier(
        `Copie impossible depuis ce navigateur : sélectionnez ${quoi.toLowerCase()} à la main.`,
        'erreur',
      );
    }
  }

  const [moi, setMoi] = useState<UtilisateurCourant | null>(null);
  const [etat, setEtat] = useState<EtatDoubleAuthentification | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  // Activation en trois temps : secret → code de confirmation → codes de secours.
  const [secret, setSecret] = useState<{ secret: string; uri: string } | null>(null);
  const [code, setCode] = useState('');
  const [codesSecours, setCodesSecours] = useState<string[] | null>(null);
  const [copieConfirmee, setCopieConfirmee] = useState(false);
  const [enCours, setEnCours] = useState(false);

  // Désactivation et régénération réclament le mot de passe.
  const [motDePasse, setMotDePasse] = useState('');

  const recharger = useCallback(async () => {
    try {
      setMoi(await api.requete<UtilisateurCourant>('/auth/me'));
      setEtat(await api.requete<EtatDoubleAuthentification>('/auth/2fa/status'));
    } catch (e) {
      setErreur(messageErreur(e));
    }
  }, [api]);

  useEffect(() => { void recharger(); }, [recharger]);

  async function commencer() {
    setErreur(null);
    setInfo(null);
    setEnCours(true);
    try {
      setSecret(await api.requete<{ secret: string; uri: string }>('/auth/2fa/setup', {
        methode: 'POST',
      }));
    } catch (e) {
      setErreur(messageErreur(e));
    } finally {
      setEnCours(false);
    }
  }

  async function activer(evenement: FormEvent) {
    evenement.preventDefault();
    setErreur(null);
    setEnCours(true);
    try {
      const reponse = await api.requete<{ recoveryCodes: string[] }>('/auth/2fa/enable', {
        methode: 'POST',
        corps: { code },
      });
      setCodesSecours(reponse.recoveryCodes);
      setSecret(null);
      setCode('');
      await recharger();
    } catch (e) {
      setErreur(messageErreur(e));
    } finally {
      setEnCours(false);
    }
  }

  // Sans événement : c'est un `type="button"` dans le formulaire de
  // régénération, il ne soumet rien.
  async function desactiver() {
    setErreur(null);
    setInfo(null);
    setEnCours(true);
    try {
      await api.requete('/auth/2fa/disable', { methode: 'POST', corps: { password: motDePasse } });
      setMotDePasse('');
      setInfo('La double authentification est désactivée.');
      await recharger();
    } catch (e) {
      setErreur(messageErreur(e));
    } finally {
      setEnCours(false);
    }
  }

  async function regenerer(evenement: FormEvent) {
    evenement.preventDefault();
    setErreur(null);
    setInfo(null);
    setEnCours(true);
    try {
      const reponse = await api.requete<{ recoveryCodes: string[] }>(
        '/auth/2fa/recovery-codes',
        { methode: 'POST', corps: { password: motDePasse } },
      );
      setMotDePasse('');
      setCopieConfirmee(false);
      setCodesSecours(reponse.recoveryCodes);
      await recharger();
    } catch (e) {
      setErreur(messageErreur(e));
    } finally {
      setEnCours(false);
    }
  }

  if (!moi || !etat) {
    if (erreur) return <Message ton="erreur">{erreur}</Message>;
    return (
      <>
        <EnTetePage titre="Sécurité" />
        <div className="flex flex-col gap-4">
          <Squelette className="h-32" />
          <Squelette className="h-44" />
        </div>
      </>
    );
  }

  // ── Codes de secours : écran bloquant ─────────────────────────────────────
  if (codesSecours) {
    return (
      <>
        <EnTetePage
          titre="Vos codes de secours"
          contexte="Cette page ne sera plus jamais affichée."
        />
        <Carte>
          <div className="flex flex-col gap-5">
            <Message ton="attention" titre="À conserver hors de votre téléphone">
              Si vous perdez votre application d'authentification, ces codes sont
              le seul moyen de reprendre la main. Chacun ne sert qu'une fois.
            </Message>

            <div>
              <div className="mb-2 flex items-center justify-between gap-3">
                <h2>{`${codesSecours.length} codes`}</h2>
                <Bouton
                  variante="secondary"
                  taille="sm"
                  icone={<IconeDupliquer taille={15} />}
                  onClick={() => void copierEtSignaler(codesSecours.join('\n'), 'Les codes')}
                >
                  Tout copier
                </Bouton>
              </div>
              {/* Chasse fixe et fond contrasté : ces codes se recopient à la
                  main sur un papier, et « 0 » contre « O » doit se distinguer
                  du premier coup d'œil. */}
              <ul className="grid gap-1.5 sm:grid-cols-2">
                {codesSecours.map((c) => (
                  <li
                    key={c}
                    className="rounded-md bg-[var(--color-surface-raised)] px-2.5 py-1.5 text-center font-mono text-[13px] tracking-wider ring-1 ring-inset ring-[var(--color-border)]"
                  >
                    {c}
                  </li>
                ))}
              </ul>
            </div>

            <CaseACocher
              libelle="J'ai enregistré ces codes en lieu sûr."
              description="Le bouton ci-dessous reste inactif tant que la case n'est pas cochée."
              checked={copieConfirmee}
              onChange={(e) => setCopieConfirmee(e.target.checked)}
            />

            {/*
              * Fermeture conditionnée à la case cochée. Un simple « Continuer »
              * se clique par réflexe : c'est exactement comme cela qu'on perd
              * l'accès à son compte trois mois plus tard.
              */}
            <div>
              <Bouton
                variante="primary"
                disabled={!copieConfirmee}
                icone={<IconeCheck taille={16} />}
                onClick={() => { setCodesSecours(null); setCopieConfirmee(false); }}
              >
                J'ai terminé
              </Bouton>
            </div>
          </div>
        </Carte>
      </>
    );
  }

  const restants = etat.remainingRecoveryCodes;

  return (
    <>
      <EnTetePage
        titre="Sécurité"
        contexte={
          etat.enabled
            ? 'Double authentification active.'
            : 'Double authentification inactive.'
        }
      />

      <div className="flex flex-col gap-4">
        {erreur && <Message ton="erreur">{erreur}</Message>}
        {info && <Message ton="succes">{info}</Message>}

        <Carte titre="Votre compte">
          <dl className="grid gap-x-8 gap-y-4 sm:grid-cols-2">
            <div>
              <dt className="text-[11px] font-medium uppercase tracking-wide text-[var(--color-fg-muted)]">
                Adresse
              </dt>
              <dd className="mt-1 break-all text-[15px] font-semibold">{moi.email}</dd>
            </div>
            <div>
              <dt className="text-[11px] font-medium uppercase tracking-wide text-[var(--color-fg-muted)]">
                Adresse confirmée
              </dt>
              <dd className="mt-1">
                {moi.emailVerified
                  ? <Etiquette ton="succes">Confirmée</Etiquette>
                  : <Etiquette ton="attention">En attente</Etiquette>}
              </dd>
            </div>
          </dl>
        </Carte>

        <Carte
          titre="Double authentification"
          description="Un code à usage unique demandé à la connexion, en plus du mot de passe."
          action={
            etat.enabled
              ? <Etiquette ton="succes">Active</Etiquette>
              : <Etiquette ton="attention">Inactive</Etiquette>
          }
        >
          {!etat.enabled && !secret && (
            <div className="flex flex-col gap-4">
              <Message>
                Un code à six chiffres sera demandé à chaque connexion, en plus de
                votre mot de passe. Prévoyez une application d'authentification
                sur votre téléphone.
              </Message>
              <div>
                <Bouton
                  variante="primary"
                  icone={<IconeSecurite taille={16} />}
                  onClick={() => void commencer()}
                  chargement={enCours}
                >
                  Activer
                </Bouton>
              </div>
            </div>
          )}

          {secret && (
            <form onSubmit={activer} className="flex flex-col gap-4">
              <Message>
                Scannez ce QR code avec votre application d'authentification
                (Google Authenticator, Microsoft Authenticator…), puis saisissez le
                code à six chiffres qu'elle affiche.
              </Message>

              <div className="flex flex-wrap items-start gap-5">
                <div className="rounded-lg border border-[var(--color-border)] bg-white p-2">
                  <QrCode texte={secret.uri} libelle="QR code à scanner avec l'application d'authentification" />
                </div>

                {/* Clé en clair, en secours : sur un poste sans téléphone à
                    portée, c'est le seul moyen d'ajouter le compte. */}
                <details className="min-w-0 flex-1 text-[13px]">
                  <summary className="cursor-pointer text-[var(--color-fg-muted)]">
                    Impossible de scanner ? Saisir la clé à la main
                  </summary>
                  <div className="mt-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-raised)] p-3">
                    <p className="text-[11px] font-medium uppercase tracking-wide text-[var(--color-fg-muted)]">
                      Clé à saisir
                    </p>
                    <div className="mt-1.5 flex flex-wrap items-center gap-2">
                      <p className="break-all font-mono text-sm tracking-wider">
                        {secret.secret}
                      </p>
                      <Bouton
                        variante="ghost"
                        taille="sm"
                        icone={<IconeDupliquer taille={15} />}
                        onClick={() => void copierEtSignaler(secret.secret, 'La clé')}
                      >
                        Copier
                      </Bouton>
                    </div>
                  </div>
                </details>
              </div>

              <Champ
                libelle="Code de vérification"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                autoComplete="one-time-code"
                inputMode="numeric"
                required
              />

              <div className="flex flex-wrap gap-2">
                <Bouton type="submit" variante="primary" chargement={enCours}>
                  Confirmer l'activation
                </Bouton>
                <Bouton
                  variante="ghost"
                  onClick={() => { setSecret(null); setCode(''); }}
                >
                  Annuler
                </Bouton>
              </div>
            </form>
          )}

          {etat.enabled && (
            <div className="flex flex-col gap-5">
              <Message ton={restants === 0 ? 'erreur' : restants <= 2 ? 'attention' : 'info'}>
                {restants === 0
                  ? "Vous n'avez plus aucun code de secours : régénérez-en dès maintenant."
                  // Accord fait ici plutôt qu'un « code(s) » qui trahit le
                  // gabarit : il ne reste jamais qu'un nombre connu de codes.
                  : `Il vous reste ${restants} code${restants > 1 ? 's' : ''} de secours.`}
              </Message>

              <form onSubmit={regenerer} className="flex flex-col gap-3">
                <div className="max-w-sm">
                  <Champ
                    libelle="Mot de passe"
                    type="password"
                    value={motDePasse}
                    onChange={(e) => setMotDePasse(e.target.value)}
                    autoComplete="current-password"
                    aide="Requis pour régénérer vos codes ou désactiver la double authentification."
                    required
                  />
                </div>
                <div className="flex flex-wrap gap-2">
                  <Bouton type="submit" variante="secondary" chargement={enCours}>
                    Régénérer les codes de secours
                  </Bouton>
                  <Bouton
                    variante="danger"
                    chargement={enCours}
                    onClick={() => void desactiver()}
                  >
                    Désactiver
                  </Bouton>
                </div>
              </form>
            </div>
          )}
        </Carte>
      </div>
    </>
  );
}
